/*###########################################################################
// Swup page transitions + sliding nav indicator
//#########################################################################*/

import Swup from 'swup'
import SwupHeadPlugin from '@swup/head-plugin'
import { dispatchPendingWalkthroughSignal } from '@js/components/_walkthrough'
import { getSavedPokedexListUrl } from '@js/components/_pokedexInfinite'

const NAV_SEL = '[data-spa-nav]'

function alpine() {
  return window.Alpine
}

function navKeyFromPath(pathname) {
  if (pathname.startsWith('/account/import-export')) return 'import-export'
  if (pathname.startsWith('/account/alerts')) return 'alerts'
  if (pathname.startsWith('/account/settings')) return 'settings'
  if (pathname.startsWith('/account/pokedex')) return 'pokedex'
  if (pathname.startsWith('/account/binders')) return 'binders'
  if (pathname.startsWith('/account/decks')) return 'decks'
  if (pathname.startsWith('/account/cards')) return 'cards'
  return null
}

function navSubFromPath(pathname) {
  if (pathname.startsWith('/account/import-export/tcg-collector')) return 'tcg-collector'
  if (pathname.startsWith('/account/import-export/collectr')) return 'collectr'
  if (pathname.startsWith('/account/import-export/cardmarket')) return 'cardmarket'
  if (pathname.startsWith('/account/settings/delivery')) return 'delivery'
  if (pathname.startsWith('/account/settings/display')) return 'display'
  if (pathname.startsWith('/account/settings/profile') || pathname === '/account/settings') return 'profile'
  if (pathname.startsWith('/account/decks')) return 'decks-all'
  return null
}

function updateNavActive(pathname = window.location.pathname) {
  const nav = document.querySelector(NAV_SEL)
  if (!nav) return

  const key = navKeyFromPath(pathname)
  nav.querySelectorAll('.nav-island__item[data-nav]').forEach((item) => {
    const active = item.dataset.nav === key
    item.classList.toggle('is-active', active)
    if (active) item.setAttribute('aria-current', 'page')
    else item.removeAttribute('aria-current')
  })

  const sub = navSubFromPath(pathname)
  nav.querySelectorAll('[data-nav-sub]').forEach((item) => {
    const active = item.dataset.navSub === sub
    item.classList.toggle('is-active', active)
    if (active) item.setAttribute('aria-current', 'page')
    else item.removeAttribute('aria-current')
  })

  closeNavFlyouts()
  requestAnimationFrame(() => moveIndicator())
}

function updateBackLink() {
  const link = document.querySelector('[data-header-back]')
  if (!link) return

  const marker = document.querySelector('#swup [data-page-back]')
  let href = marker?.dataset.backHref
  const label = marker?.dataset.backLabel

  // On a species page, prefer the exact (possibly filtered/searched) Pokédex
  // list URL the user came from, so the button reproduces that view rather
  // than always resetting to the unfiltered index.
  if (href && window.location.pathname.startsWith('/account/pokedex/')) {
    href = getSavedPokedexListUrl() || href
  }

  if (href) {
    link.href = href
    link.hidden = false
    const labelEl = link.querySelector('[data-header-back-label]')
    if (labelEl) labelEl.textContent = label || 'Back'
  } else {
    link.hidden = true
    link.removeAttribute('href')
  }
}

function setFlyoutOpen(flyout, open) {
  if (!flyout) return
  flyout.classList.toggle('is-open', open)
  const trigger = flyout.querySelector('[data-flyout-trigger]')
  if (trigger) trigger.setAttribute('aria-expanded', open ? 'true' : 'false')
}

function closeNavFlyouts() {
  document.querySelectorAll('[data-nav-flyout].is-open').forEach((flyout) => {
    setFlyoutOpen(flyout, false)
  })
}

function initNavFlyouts() {
  const hoverMq = window.matchMedia('(hover: hover) and (pointer: fine)')
  const canHover = () => hoverMq.matches

  document.querySelectorAll('[data-nav-flyout]').forEach((flyout) => {
    if (flyout.dataset.bound) return
    flyout.dataset.bound = '1'

    const trigger = flyout.querySelector('[data-flyout-trigger]')
    if (!trigger) return

    flyout.addEventListener('mouseenter', () => {
      if (canHover()) setFlyoutOpen(flyout, true)
    })

    flyout.addEventListener('mouseleave', () => {
      if (!canHover()) return
      // Don’t close Decks import while the paste field is focused.
      if (flyout.hasAttribute('data-decks-flyout')) {
        const active = document.activeElement
        if (active && flyout.contains(active) && (active.tagName === 'TEXTAREA' || active.tagName === 'INPUT')) {
          return
        }
      }
      setFlyoutOpen(flyout, false)
    })

    trigger.addEventListener('click', (e) => {
      // Touch: optional hold opens menu first; Import/Export always navigates.
      if (canHover() || !trigger.hasAttribute('data-flyout-hold')) return

      if (!flyout.classList.contains('is-open')) {
        e.preventDefault()
        setFlyoutOpen(flyout, true)
      }
    })
  })

  if (window.__navFlyoutsDocBound) return
  window.__navFlyoutsDocBound = true

  document.addEventListener('click', (e) => {
    document.querySelectorAll('[data-nav-flyout].is-open').forEach((flyout) => {
      if (!flyout.contains(e.target)) setFlyoutOpen(flyout, false)
    })
  })

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeNavFlyouts()
  })
}

function moveIndicator() {
  const nav = document.querySelector(NAV_SEL)
  if (!nav) return

  const shell = nav.querySelector('.nav-island__shell')
  const indicator = nav.querySelector('.nav-island__indicator')
  const active = nav.querySelector('.nav-island__item.is-active:not(.nav-island__item--ghost)')

  if (!shell || !indicator) return

  if (!active) {
    indicator.style.opacity = '0'
    return
  }

  const shellRect = shell.getBoundingClientRect()
  const activeRect = active.getBoundingClientRect()

  indicator.style.width = `${activeRect.width}px`
  indicator.style.height = `${activeRect.height}px`
  indicator.style.transform = `translate(${activeRect.left - shellRect.left}px, ${activeRect.top - shellRect.top}px)`
  indicator.style.opacity = '1'
}

function destroyAlpineInSwup() {
  const el = document.querySelector('#swup')
  const A = alpine()
  if (!el || !A?.destroyTree) return
  try {
    A.destroyTree(el)
  } catch {
    // ignore
  }
}

function initAlpineInSwup() {
  const el = document.querySelector('#swup')
  const A = alpine()
  if (!el || !A?.initTree) return
  try {
    A.initTree(el)
  } catch (e) {
    console.warn('Alpine reinit failed', e)
  }

}

function isAccountPath(url) {
  try {
    const parsed = typeof url === 'string' ? new URL(url, window.location.origin) : url
    return parsed.pathname.startsWith('/account')
  } catch {
    return false
  }
}

export function initSpaNav() {
  const swup = new Swup({
    containers: ['#swup'],
    animateHistoryBrowsing: true,
    plugins: [
      new SwupHeadPlugin({
        persistAssets: true,
      }),
    ],
    ignoreVisit: (url, { el } = {}) => {
      if (el?.hasAttribute('data-no-spa') || el?.hasAttribute('download')) return true
      if (el?.target && el.target !== '_self') return true
      return !isAccountPath(url)
    },
  })

  // Avoid caching Alpine-rendered DOM from the first paint
  swup.cache.clear()

  swup.hooks.before('content:replace', () => {
    destroyAlpineInSwup()
    // Drop scroll locks left by abandoned Alpine modals outside #swup.
    if (window.app) {
      window.app._scrollLocks = 0
      document.documentElement.style.overflow = ''
    }
  })

  swup.hooks.on('content:replace', () => {
    initAlpineInSwup()
    dispatchPendingWalkthroughSignal(document.querySelector('#swup') || document)
  })

  swup.hooks.on('page:view', () => {
    updateNavActive(window.location.pathname)
    updateBackLink()
    window.dispatchEvent(new CustomEvent('app:navigated', {
      detail: { url: new URL(window.location.href) },
    }))
  })

  initNavFlyouts()
  updateNavActive(window.location.pathname)
  updateBackLink()
  moveIndicator()
  window.addEventListener('resize', () => moveIndicator())
  setTimeout(moveIndicator, 100)
  setTimeout(moveIndicator, 400)

  window.swup = swup
  return swup
}
